"use client"

import * as React from "react"
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { XIcon } from "lucide-react"
import { useAnalyticsSurface } from "@/components/user-analytics/user-analytics-provider"

type DialogDismiss = () => void

const DialogDismissContext = React.createContext<DialogDismiss | null>(null)

type AnalyticsRootProps = { analyticsFeatureId?: string; analyticsParentFeatureId?: string }

function Dialog({
  onOpenChange,
  analyticsFeatureId,
  analyticsParentFeatureId: _analyticsParentFeatureId,
  actionsRef: actionsRefProp,
  confirmCloseWhenDirty,
  ...props
}: DialogPrimitive.Root.Props & AnalyticsRootProps & {
  /** Opt in to confirming user dismissal; successful saves can still close via `open`. */
  confirmCloseWhenDirty?: boolean
}) {
  const [internalOpen, setInternalOpen] = React.useState(Boolean(props.defaultOpen))
  const isOpen = props.open ?? internalOpen
  const [confirmingClose, setConfirmingClose] = React.useState(false)
  const closeConfirmedRef = React.useRef(false)
  React.useEffect(() => {
    if (!isOpen) setConfirmingClose(false)
  }, [isOpen])
  useAnalyticsSurface(analyticsFeatureId ?? "", Boolean(analyticsFeatureId && isOpen))
  const handleOpenChange: NonNullable<DialogPrimitive.Root.Props["onOpenChange"]> = (...args) => {
    if (!args[0] && confirmCloseWhenDirty && !closeConfirmedRef.current) {
      args[1].cancel()
      setConfirmingClose(true)
      return
    }
    setInternalOpen(args[0])
    onOpenChange?.(...args)
  }
  const localActionsRef = React.useRef<DialogPrimitive.Root.Actions | null>(null)
  // Prefer consumer ref when provided; otherwise own a stable local ref.
  const actionsRef = (actionsRefProp ??
    localActionsRef) as React.RefObject<DialogPrimitive.Root.Actions | null>
  // Indirection so dismiss stays referentially stable across renders even if the
  // active RefObject switches (e.g. parent starts/stops passing actionsRef).
  const actionsRefBox = React.useRef(actionsRef)
  actionsRefBox.current = actionsRef

  const dismiss = React.useCallback(() => {
    // Same path as Escape: store.setOpen with event details, then consumer onOpenChange.
    actionsRefBox.current.current?.close()
  }, [])

  const children = props.children
  const confirmation = isOpen && confirmingClose ? (
    <Dialog open onOpenChange={(open) => { if (!open) setConfirmingClose(false) }}>
      <DialogContent elevated className="max-w-md">
        <DialogHeader>
          <DialogTitle>Unsaved changes</DialogTitle>
          <DialogDescription>You have unsaved edits. Discard them or keep editing.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setConfirmingClose(false)}>
            Keep editing
          </Button>
          <Button type="button" variant="destructive" onClick={() => {
            setConfirmingClose(false)
            closeConfirmedRef.current = true
            try {
              dismiss()
            } finally {
              closeConfirmedRef.current = false
            }
          }}>
            Discard changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ) : null
  const dialogChildren: DialogPrimitive.Root.Props["children"] = confirmCloseWhenDirty !== undefined
    ? typeof children === "function"
      ? (payload) => <>{children(payload)}{confirmation}</>
      : <>{children}{confirmation}</>
    : children

  return (
    <DialogDismissContext.Provider value={dismiss}>
      <DialogPrimitive.Root
        data-slot="dialog"
        onOpenChange={handleOpenChange}
        actionsRef={actionsRef}
        {...props}
        open={confirmCloseWhenDirty === undefined ? props.open : isOpen}
        children={dialogChildren}
      />
    </DialogDismissContext.Provider>
  )
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

const dialogOverlayClassName =
  "fixed inset-0 isolate bg-black/40 duration-100 backdrop-blur-sm motion-safe:data-open:animate-in motion-safe:data-open:fade-in-0 motion-safe:data-closed:animate-out motion-safe:data-closed:fade-out-0"

function DialogOverlay({
  className,
  elevated,
  style,
  ...props
}: DialogPrimitive.Backdrop.Props & {
  /** Stack above another open dialog (e.g. confirm from report editor). */
  elevated?: boolean
}) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      data-elevated={elevated ? "" : undefined}
      // Base UI skips Backdrop for nested dialogs unless forceRender — elevated
      // nested modals need a visible dim/blur layer above the parent dialog.
      forceRender={elevated}
      className={cn(
        dialogOverlayClassName,
        elevated ? "z-[60]" : "z-50",
        className
      )}
      {...props}
      style={elevated ? { ...style, zIndex: 60 } : style}
    />
  )
}

/**
 * Nested/elevated dialogs: Base UI outside-press often misses the force-rendered
 * backdrop (focus management marks it data-base-ui-inert). Dismiss via Root
 * actionsRef.close() so scrim click closes only this dialog.
 */
function ElevatedDialogOverlay({
  className,
  style,
  onClick,
  ...props
}: Omit<DialogPrimitive.Backdrop.Props, "forceRender">) {
  const dismiss = React.useContext(DialogDismissContext)

  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      data-elevated=""
      forceRender
      className={cn(dialogOverlayClassName, "z-[60]", className)}
      {...props}
      style={{ ...style, zIndex: 60 }}
      onClick={(event) => {
        onClick?.(event)
        if (event.defaultPrevented) return
        if (event.target !== event.currentTarget) return
        dismiss?.()
      }}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  closeButtonClassName,
  elevated = false,
  layout = "centered",
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
  closeButtonClassName?: string
  /** Stack above another open dialog (e.g. confirm from report editor). */
  elevated?: boolean
  /** viewport-inset: top/bottom margin (tall modals). centered: default modal. */
  layout?: "centered" | "viewport-inset"
}) {
  return (
    <DialogPortal>
      {elevated ? <ElevatedDialogOverlay /> : <DialogOverlay />}
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        data-elevated={elevated ? "" : undefined}
        className={cn(
          "fixed left-1/2 w-full max-w-[calc(100%-2rem)] -translate-x-1/2 gap-6 rounded-xl bg-popover p-6 text-sm text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none max-w-lg data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
          elevated ? "z-[61]" : "z-50",
          layout === "centered" && "top-1/2 grid -translate-y-1/2 data-open:zoom-in-95 data-closed:zoom-out-95",
          layout === "viewport-inset" &&
            "top-[calc(var(--app-chrome-header-height)+0.5rem)] right-auto bottom-2 flex translate-y-0 max-h-[calc(100dvh-var(--app-chrome-header-height)-1rem)]",
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={
              <Button
                variant="ghost"
                className={cn("absolute top-4 right-4", closeButtonClassName)}
                size="icon-sm"
              />
            }
          >
            <XIcon
            />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>
          Close
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("leading-none font-medium", className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground",
        className
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
